import React, { useMemo } from 'react';
import { Text, Group, Rect } from 'react-konva';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  LAYER_HEIGHT,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_WIDTH,
  FLASHBACK_COVER_LOGO_HEIGHT,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_OPACITY,
  ALLOCINE_LOGO_WIDTH,
  ALLOCINE_LOGO_HEIGHT,
  ALLOCINE_LOGO_BOTTOM_MARGIN,
  FLASHBACK_BACKGROUND_COLOR,
  FLASHBACK_TEXT_COLOR,
  FLASHBACK_FONT_FAMILY,
  FLASHBACK_REPARTITION_TITLE_FONT_SIZE,
  FLASHBACK_REPARTITION_TITLE_FONT_STYLE,
  FLASHBACK_REPARTITION_TITLE_LINE_HEIGHT,
  FLASHBACK_REPARTITION_MEMBER_CARD_WIDTH,
  FLASHBACK_REPARTITION_MEMBER_CARD_HEIGHT,
  FLASHBACK_REPARTITION_MEMBER_CARD_ROTATION,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_ROTATION,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_SIZE,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_STYLE,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_LINE_HEIGHT,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_X,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y_EXTRA,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_FAMILY,
  FLASHBACK_REPARTITION_COVER_LOGO_TO_TITLE_SPACING,
  FLASHBACK_REPARTITION_TITLE_TO_CARD_SPACING,
  FLASHBACK_REPARTITION_GAUGE_TO_BOTTOM_LOGO_SPACING,
  FLASHBACK_REPARTITION_GAUGE_BAR_WIDTH,
  FLASHBACK_REPARTITION_GAUGE_BAR_HEIGHT,
  FLASHBACK_REPARTITION_GAUGE_WIDTH,
  FLASHBACK_REPARTITION_GAUGE_COLUMN_WIDTH,
  FLASHBACK_REPARTITION_GAUGE_BAR_CORNER_RADIUS,
  FLASHBACK_REPARTITION_GAUGE_TICKET_WIDTH,
  FLASHBACK_REPARTITION_GAUGE_TICKET_HEIGHT,
  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_SIZE,
  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_STYLE,
  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_FAMILY,
  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_LINE_HEIGHT,
  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TOP_MARGIN,
  FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_SIZE,
  FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_STYLE,
  FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_FAMILY,
  FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_LINE_HEIGHT,
  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TO_LABEL_SPACING,
  FLASHBACK_REPARTITION_GAUGE_TICKET_ROTATION,
  FLASHBACK_REPARTITION_GAUGE_TICKET_TO_BAR_SPACING,
  FLASHBACK_REPARTITION_MOVIE_BAR_COLOR_STOPS,
  FLASHBACK_REPARTITION_MOVIE_COUNT_COLOR,
  FLASHBACK_REPARTITION_MOVIE_LABEL_COLOR,
  FLASHBACK_REPARTITION_SERIES_BAR_COLOR_STOPS,
  FLASHBACK_REPARTITION_SERIES_COUNT_COLOR,
  FLASHBACK_REPARTITION_SERIES_LABEL_COLOR
} from 'common/components/RewindModal/Konvas/constants';
import { getDPRSize } from 'common/components/RewindModal/helper';
import {
  MEMBER_CARD_MOVIE,
  MEMBER_CARD_SERIES,
  MEMBER_CARD_SERIES_MOVIE,
  IMG_TICKET_MOVIE,
  IMG_TICKET_SERIES
} from 'common/configuration/constants';

type ScreenRepartitionProps = {
  data: {
    movie_count: number;
    series_count: number;
    phrase_1?: string;
    phrase_2?: string;
  };
  userNickname?: string;
  flashbackType?: number;
};

const ShareableScreenRepartition = React.forwardRef<
  any,
  ScreenRepartitionProps
>(({ data, userNickname, flashbackType }, ref) => {
  const movieCount = data?.movie_count ?? 0;
  const seriesCount = data?.series_count ?? 0;
  const isType6 = flashbackType === 6;

  const memberType = useMemo(
    () =>
      movieCount > seriesCount
        ? 'movie'
        : seriesCount > movieCount
          ? 'series'
          : 'seriemovie',
    [movieCount, seriesCount]
  );

  const memberCardImage = useMemo(
    () =>
      memberType === 'movie'
        ? MEMBER_CARD_MOVIE
        : memberType === 'series'
          ? MEMBER_CARD_SERIES
          : MEMBER_CARD_SERIES_MOVIE,
    [memberType]
  );

  const moviesBarPercent = useMemo(() => {
    if (movieCount === seriesCount) return 50;
    if (movieCount > seriesCount) {
      const reducedPercent = 100 - 100 / 3;
      return Math.round(reducedPercent * 1.1);
    }
    let percent = Math.round((movieCount / (movieCount + seriesCount)) * 100);
    if (percent < 15) percent += 10;
    return percent;
  }, [movieCount, seriesCount]);

  const seriesBarPercent = useMemo(() => {
    if (seriesCount === movieCount) return 50;
    if (seriesCount > movieCount) {
      const reducedPercent = 100 - 100 / 3;
      return Math.round(reducedPercent * 1.1);
    }
    let percent = Math.round((seriesCount / (movieCount + seriesCount)) * 100);
    if (percent < 15) percent += 10;
    return percent;
  }, [movieCount, seriesCount]);

  const movieLabel = movieCount > 1 ? 'films' : 'film';
  const seriesLabel = seriesCount > 1 ? 'séries' : 'série';

  const maxBarPercent = useMemo(() => {
    return Math.max(moviesBarPercent, seriesBarPercent);
  }, [moviesBarPercent, seriesBarPercent]);

  const TYPE6_TITLE_FONT_SIZE = getDPRSize(28);
  const TYPE6_SUBTITLE_FONT_SIZE = getDPRSize(17);
  const TYPE6_TITLE_FONT_WEIGHT = '800';
  const TYPE6_SUBTITLE_FONT_WEIGHT = '500';
  const TYPE6_SUBTITLE_COLOR = '#E0E0E0';
  const TYPE6_SPACING_LOGO_TO_TITLE = getDPRSize(70);
  const TYPE6_SPACING_TITLE_TO_SUBTITLE = getDPRSize(80);
  const TYPE6_SPACING_SUBTITLE_TO_GAUGE = getDPRSize(40);

  const layout = useMemo(() => {
    const title1Height =
      FLASHBACK_REPARTITION_TITLE_FONT_SIZE *
      FLASHBACK_REPARTITION_TITLE_LINE_HEIGHT;
    const title2Height =
      FLASHBACK_REPARTITION_TITLE_FONT_SIZE *
      FLASHBACK_REPARTITION_TITLE_LINE_HEIGHT;

    const spacing_cover_logo_to_title =
      FLASHBACK_REPARTITION_COVER_LOGO_TO_TITLE_SPACING;
    const spacing_title1_to_card = FLASHBACK_REPARTITION_TITLE_TO_CARD_SPACING;
    const spacing_card_to_title2 = BLOCK_MARGIN * 1.5;
    const spacing_title2_to_gauge = BLOCK_MARGIN * 1.5;

    let y_title1: number;
    let y_card: number;
    let y_title2: number;

    if (isType6) {
      // Pour le type 6, espacement logo -> title = 30px
      y_title1 =
        FLASHBACK_COVER_LOGO_TOP_MARGIN +
        FLASHBACK_COVER_LOGO_HEIGHT +
        TYPE6_SPACING_LOGO_TO_TITLE;

      // Calcul de la hauteur du title (phrase_1)
      const phrase1Height = TYPE6_TITLE_FONT_SIZE * 1.2;

      // Calcul de la hauteur du subtitle (phrase_2)
      const phrase2Height = TYPE6_SUBTITLE_FONT_SIZE * 1.2;
      // y_title2 pour "La preuve en chiffres :"
      y_title2 =
        y_title1 +
        phrase1Height +
        TYPE6_SPACING_TITLE_TO_SUBTITLE +
        phrase2Height +
        TYPE6_SPACING_SUBTITLE_TO_GAUGE;
      y_card = y_title2;
    } else {
      y_title1 =
        FLASHBACK_COVER_LOGO_TOP_MARGIN +
        FLASHBACK_COVER_LOGO_HEIGHT +
        spacing_cover_logo_to_title;
      y_card = y_title1 + title1Height + spacing_title1_to_card;
      y_title2 =
        y_card +
        FLASHBACK_REPARTITION_MEMBER_CARD_HEIGHT +
        spacing_card_to_title2;
    }

    const y_gauge =
      y_title2 + title2Height + (isType6 ? 0 : spacing_title2_to_gauge);

    return { y_title1, y_card, y_title2, y_gauge };
  }, [isType6]);

  const gaugeX = (LAYER_WIDTH - FLASHBACK_REPARTITION_GAUGE_WIDTH) / 2;

  const memberCardX = !isType6
    ? LAYER_WIDTH / 2 - FLASHBACK_REPARTITION_MEMBER_CARD_WIDTH / 2
    : 0;
  const memberCardY = !isType6 ? layout.y_card : 0;

  const barMovieX = gaugeX;
  const barSeriesX = gaugeX + FLASHBACK_REPARTITION_GAUGE_COLUMN_WIDTH;
  const ticketOffsetX =
    (FLASHBACK_REPARTITION_GAUGE_BAR_WIDTH -
      FLASHBACK_REPARTITION_GAUGE_TICKET_WIDTH) /
    2;
  const ticketMovieX = barMovieX;
  const ticketSeriesX = barSeriesX;
  const ticketWidth = FLASHBACK_REPARTITION_GAUGE_TICKET_WIDTH;
  const ticketHeight = FLASHBACK_REPARTITION_GAUGE_TICKET_HEIGHT;

  const barHeight = FLASHBACK_REPARTITION_GAUGE_BAR_HEIGHT;
  const bottomLogoY =
    LAYER_HEIGHT - ALLOCINE_LOGO_HEIGHT - ALLOCINE_LOGO_BOTTOM_MARGIN;
  const barY = bottomLogoY - FLASHBACK_REPARTITION_GAUGE_TO_BOTTOM_LOGO_SPACING;
  const maxBarTop = barY - (barHeight * maxBarPercent) / 100;
  const ticketY =
    maxBarTop +
    FLASHBACK_REPARTITION_GAUGE_TICKET_TO_BAR_SPACING -
    ticketHeight;

  return (
    <ShareableStage ref={ref}>
      <FlashbackLayout
        backgroundColor={FLASHBACK_BACKGROUND_COLOR}
        backgroundOpacity={1}
        coverLogoWidth={FLASHBACK_COVER_LOGO_WIDTH}
        coverLogoHeight={FLASHBACK_COVER_LOGO_HEIGHT}
        coverLogoTopMargin={FLASHBACK_COVER_LOGO_TOP_MARGIN}
        coverLogoOpacity={FLASHBACK_COVER_LOGO_OPACITY}
        allocineLogoWidth={ALLOCINE_LOGO_WIDTH}
        allocineLogoHeight={ALLOCINE_LOGO_HEIGHT}
        allocineLogoBottomMargin={ALLOCINE_LOGO_BOTTOM_MARGIN}
      >
        <Group>
          {isType6 ? (
            <>
              {data?.phrase_1 && (
                <Text
                  text={data.phrase_1}
                  fontSize={TYPE6_TITLE_FONT_SIZE}
                  fontFamily="Montserrat, Arial, sans-serif"
                  fontStyle={TYPE6_TITLE_FONT_WEIGHT}
                  lineHeight={1.2}
                  fill={FLASHBACK_TEXT_COLOR}
                  width={LAYER_WIDTH - BLOCK_MARGIN * 4}
                  x={BLOCK_MARGIN * 2}
                  align="center"
                  y={layout.y_title1}
                />
              )}
              {data?.phrase_2 && (
                <Text
                  text={data.phrase_2}
                  fontSize={TYPE6_SUBTITLE_FONT_SIZE}
                  fontFamily="Montserrat, Arial, sans-serif"
                  fontStyle={TYPE6_SUBTITLE_FONT_WEIGHT}
                  lineHeight={1.2}
                  fill={TYPE6_SUBTITLE_COLOR}
                  width={LAYER_WIDTH - BLOCK_MARGIN * 4}
                  x={BLOCK_MARGIN * 2}
                  align="center"
                  y={
                    layout.y_title1 +
                    TYPE6_TITLE_FONT_SIZE * 1.2 +
                    TYPE6_SPACING_TITLE_TO_SUBTITLE
                  }
                />
              )}
            </>
          ) : (
            <>
              <Text
                text="Vous avez mérité votre carte de membre"
                fontSize={FLASHBACK_REPARTITION_TITLE_FONT_SIZE}
                fontFamily={FLASHBACK_FONT_FAMILY}
                fontStyle={FLASHBACK_REPARTITION_TITLE_FONT_STYLE}
                lineHeight={FLASHBACK_REPARTITION_TITLE_LINE_HEIGHT}
                fill={FLASHBACK_TEXT_COLOR}
                width={LAYER_WIDTH - BLOCK_MARGIN * 4}
                x={BLOCK_MARGIN * 2}
                align="center"
                y={layout.y_title1}
              />

              <Group
                x={memberCardX}
                y={memberCardY}
                rotation={FLASHBACK_REPARTITION_MEMBER_CARD_ROTATION}
              >
                <KonvaImage
                  path={memberCardImage}
                  isLocal={true}
                  width={FLASHBACK_REPARTITION_MEMBER_CARD_WIDTH}
                  height={FLASHBACK_REPARTITION_MEMBER_CARD_HEIGHT}
                  x={0}
                  y={0}
                />
                <Text
                  text={userNickname}
                  fontSize={
                    FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_SIZE
                  }
                  fontFamily={
                    FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_FAMILY
                  }
                  fontStyle={
                    FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_STYLE
                  }
                  lineHeight={
                    FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_LINE_HEIGHT
                  }
                  fill="#000000"
                  x={FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_X}
                  y={
                    FLASHBACK_REPARTITION_MEMBER_CARD_HEIGHT -
                    FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y +
                    FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y_EXTRA
                  }
                  rotation={FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_ROTATION}
                />
              </Group>
            </>
          )}

          <Text
            text="La preuve en chiffres :"
            fontSize={FLASHBACK_REPARTITION_TITLE_FONT_SIZE}
            fontFamily={FLASHBACK_FONT_FAMILY}
            fontStyle={FLASHBACK_REPARTITION_TITLE_FONT_STYLE}
            lineHeight={FLASHBACK_REPARTITION_TITLE_LINE_HEIGHT}
            fill={FLASHBACK_TEXT_COLOR}
            width={LAYER_WIDTH - BLOCK_MARGIN * 4}
            x={BLOCK_MARGIN * 2}
            align="center"
            y={layout.y_title2}
          />

          <Group y={ticketY}>
            <Group x={ticketMovieX}>
              <KonvaImage
                path={IMG_TICKET_MOVIE}
                isLocal={true}
                width={ticketWidth}
                height={ticketHeight}
                x={ticketOffsetX}
                y={0}
              />
              <Text
                text={movieCount.toString()}
                fontSize={FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_SIZE}
                fontFamily={
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_FAMILY
                }
                fontStyle={FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_STYLE}
                lineHeight={
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_LINE_HEIGHT
                }
                fill={FLASHBACK_REPARTITION_MOVIE_COUNT_COLOR}
                width={ticketWidth}
                align="center"
                x={ticketOffsetX}
                y={FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TOP_MARGIN}
                rotation={FLASHBACK_REPARTITION_GAUGE_TICKET_ROTATION}
              />
              <Text
                text={movieLabel}
                fontSize={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_SIZE}
                fontFamily={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_FAMILY}
                fontStyle={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_STYLE}
                lineHeight={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_LINE_HEIGHT}
                fill={FLASHBACK_REPARTITION_MOVIE_LABEL_COLOR}
                width={ticketWidth}
                align="center"
                x={ticketOffsetX}
                y={
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TOP_MARGIN +
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_SIZE +
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TO_LABEL_SPACING
                }
                rotation={FLASHBACK_REPARTITION_GAUGE_TICKET_ROTATION}
              />
              <Rect
                x={0}
                y={barY - ticketY - (barHeight * moviesBarPercent) / 100}
                width={FLASHBACK_REPARTITION_GAUGE_BAR_WIDTH}
                height={(barHeight * moviesBarPercent) / 100}
                fillLinearGradientColorStops={
                  FLASHBACK_REPARTITION_MOVIE_BAR_COLOR_STOPS
                }
                fillLinearGradientStartPoint={{ x: 0, y: 0 }}
                fillLinearGradientEndPoint={{ x: 0, y: barHeight }}
                cornerRadius={[
                  0,
                  0,
                  0,
                  FLASHBACK_REPARTITION_GAUGE_BAR_CORNER_RADIUS
                ]}
              />
            </Group>

            <Group x={ticketSeriesX}>
              <KonvaImage
                path={IMG_TICKET_SERIES}
                isLocal={true}
                width={ticketWidth}
                height={ticketHeight}
                x={ticketOffsetX}
                y={0}
              />
              <Text
                text={seriesCount.toString()}
                fontSize={FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_SIZE}
                fontFamily={
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_FAMILY
                }
                fontStyle={FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_STYLE}
                lineHeight={
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_LINE_HEIGHT
                }
                fill={FLASHBACK_REPARTITION_SERIES_COUNT_COLOR}
                width={ticketWidth}
                align="center"
                x={ticketOffsetX}
                y={FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TOP_MARGIN}
                rotation={FLASHBACK_REPARTITION_GAUGE_TICKET_ROTATION}
              />
              <Text
                text={seriesLabel}
                fontSize={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_SIZE}
                fontFamily={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_FAMILY}
                fontStyle={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_FONT_STYLE}
                lineHeight={FLASHBACK_REPARTITION_GAUGE_TICKET_TEXT_LINE_HEIGHT}
                fill={FLASHBACK_REPARTITION_SERIES_LABEL_COLOR}
                width={ticketWidth}
                align="center"
                x={ticketOffsetX}
                y={
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TOP_MARGIN +
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_FONT_SIZE +
                  FLASHBACK_REPARTITION_GAUGE_TICKET_COUNT_TO_LABEL_SPACING
                }
                rotation={FLASHBACK_REPARTITION_GAUGE_TICKET_ROTATION}
              />
              <Rect
                x={0}
                y={barY - ticketY - (barHeight * seriesBarPercent) / 100}
                width={FLASHBACK_REPARTITION_GAUGE_BAR_WIDTH}
                height={(barHeight * seriesBarPercent) / 100}
                fillLinearGradientColorStops={
                  FLASHBACK_REPARTITION_SERIES_BAR_COLOR_STOPS
                }
                fillLinearGradientStartPoint={{ x: 0, y: 0 }}
                fillLinearGradientEndPoint={{ x: 0, y: barHeight }}
                cornerRadius={[
                  0,
                  0,
                  FLASHBACK_REPARTITION_GAUGE_BAR_CORNER_RADIUS,
                  0
                ]}
              />
            </Group>
          </Group>
        </Group>
      </FlashbackLayout>
    </ShareableStage>
  );
});

ShareableScreenRepartition.displayName = 'ShareableScreenRepartition';
export default ShareableScreenRepartition;
